import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { submitQuote } from '@/lib/api';

const initial = { name: '', company: '', email: '', phone: '', quantity: '', message: '' };

export default function QuoteDialog({ open, onOpenChange, product }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial);
      setSuccess(false);
      setErrors({});
    }
  }, [open]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Nom requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!form.message.trim() && !product) e.message = 'Message requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitQuote({
        ...form,
        product_slug: product?.slug || null,
        product_name: product?.name || null,
      });
      setSuccess(true);
      toast.success('Demande envoyée — nous vous répondrons sous 24-48h.');
    } catch (err) {
      toast.error('Une erreur est survenue. Réessayez ou contactez-nous directement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="quote-dialog">
        <div className="px-6 pt-6 pb-3 border-b border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Demande de devis</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {product
                ? <>Pour le produit : <span className="font-medium text-foreground">{product.name}</span></>
                : 'Parlez-nous de votre besoin, nous reviendrons vers vous sous 24-48h.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        {success ? (
          <div className="p-8 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[hsl(186_45%_92%)] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[hsl(186_55%_38%)]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold">Demande bien reçue</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Nous vous avons envoyé une confirmation par email à <span className="font-medium text-foreground">{form.email}</span>.
              </p>
            </div>
            <Button onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="quote-success-close-button">Fermer</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name">Nom complet *</Label>
                <Input id="name" value={form.name} onChange={update('name')} className="mt-1" data-testid="quote-form-name-input" />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="company">Société</Label>
                <Input id="company" value={form.company} onChange={update('company')} className="mt-1" data-testid="quote-form-company-input" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={update('email')} className="mt-1" data-testid="quote-form-email-input" />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" value={form.phone} onChange={update('phone')} className="mt-1" data-testid="quote-form-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="quantity">Quantité souhaitée</Label>
                <Input id="quantity" placeholder="Ex: 200 unités, 1 palette..." value={form.quantity} onChange={update('quantity')} className="mt-1" data-testid="quote-form-quantity-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message">Message {!product && '*'}</Label>
                <Textarea
                  id="message"
                  rows={4}
                  placeholder={product ? 'Précisez vos besoins (optionnel)' : 'Décrivez votre besoin (catégorie, marques, volumes…)'}
                  value={form.message}
                  onChange={update('message')}
                  className="mt-1"
                  data-testid="quote-form-message-input"
                />
                {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground">B2B uniquement — réponse sous 24-48h.</p>
              <Button type="submit" disabled={submitting} className="rounded-xl" data-testid="quote-form-submit-button">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer la demande
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
